import { LuCheck } from '@platform/icon';
import {
  EDUCATION_STEP,
  type EducationStep,
} from '../constants/education-step.constant';

/** 배열 순서가 곧 단계 번호다 — `EDUCATION_STEP` 값과 어긋나면 안 된다. */
const STEPS = [
  '교육과정',
  '접수 가능한 회차',
  '수강자 정보입력',
  '결제 및 접수완료',
] as const;

interface EducationProgressStepsProps {
  activeStep: EducationStep;
  /** 여기까지만 눌러서 되돌아갈 수 있다. `onStepSelect` 가 있을 때만 의미가 있다. */
  maxSelectableStep?: EducationStep;
  onStepSelect?: (step: EducationStep) => void;
}

/** 교육 탐색부터 접수 완료까지 현재 위치를 보여주는 공통 진행 단계. */
export function EducationProgressSteps({
  activeStep,
  maxSelectableStep = EDUCATION_STEP.COURSE,
  onStepSelect,
}: EducationProgressStepsProps) {
  return (
    <nav
      aria-label="교육 접수 진행 단계"
      className="border-y border-gray-200 bg-gray-50/70 px-3 py-5 dark:border-white/10 dark:bg-white/[0.025] sm:px-6"
    >
      <ol className="mx-auto grid max-w-4xl grid-cols-4">
        {STEPS.map((label, index) => {
          const step = (index + 1) as EducationStep;
          const completed = step < activeStep;
          const active = step === activeStep;
          const selectable = !!onStepSelect && step <= maxSelectableStep;
          const content = (
            <>
              <span
                className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-md border text-xs font-bold tabular-nums transition-all duration-500 sm:h-9 sm:w-9 ${
                  completed
                    ? 'border-brand-600 bg-brand-600 text-white'
                    : active
                      ? 'border-brand-600 bg-white text-brand-700 shadow-sm dark:bg-neutral-900 dark:text-brand-300'
                      : 'border-gray-300 bg-white text-gray-400 dark:border-white/15 dark:bg-neutral-900 dark:text-neutral-500'
                }`}
              >
                {completed ? (
                  <LuCheck aria-hidden className="h-4 w-4" strokeWidth={2.5} />
                ) : (
                  step
                )}
              </span>
              <span
                className={`mt-2 max-w-24 text-center text-[10px] font-medium leading-4 transition-colors duration-300 sm:max-w-none sm:text-xs ${
                  active
                    ? 'text-brand-700 dark:text-brand-300'
                    : completed
                      ? 'text-gray-700 dark:text-neutral-200'
                      : 'text-gray-400 dark:text-neutral-500'
                }`}
              >
                {label}
              </span>
            </>
          );

          return (
            <li
              key={label}
              className="relative flex min-w-0 flex-col items-center"
              aria-current={active ? 'step' : undefined}
            >
              {index < STEPS.length - 1 && (
                <span
                  aria-hidden
                  className="absolute left-[calc(50%+1rem)] right-[calc(-50%+1rem)] top-4 h-px overflow-hidden bg-gray-300 dark:bg-white/15 sm:left-[calc(50%+1.125rem)] sm:right-[calc(-50%+1.125rem)] sm:top-[1.125rem]"
                >
                  <span
                    className={`block h-full origin-left bg-brand-600 transition-transform duration-700 ease-out ${
                      step < activeStep ? 'scale-x-100' : 'scale-x-0'
                    }`}
                  />
                </span>
              )}
              {selectable ? (
                <button
                  type="button"
                  onClick={() => onStepSelect(step)}
                  className="relative z-10 flex flex-col items-center rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-500"
                  aria-label={`${step}단계 ${label}로 이동`}
                >
                  {content}
                </button>
              ) : (
                <div className="relative z-10 flex flex-col items-center">
                  {content}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
